import { CookieOptions, CookieService } from 'ngx-cookie-service';
import {
  LoadingManager,
  RepeatWrapping,
  REVISION,
  SRGBColorSpace,
  Texture,
  TextureLoader,
} from 'three';
import {
  DRACOLoader,
  GLTF,
  GLTFLoader,
  KTX2Loader,
  MeshoptDecoder,
  RGBELoader,
} from 'three-stdlib';
import { environment } from '../../../../environments/environment';
import { inject } from '@angular/core';

const cookieConfig: CookieOptions = {
  path: '/',
  secure: !environment.dev,
  sameSite: 'Lax',
  domain: environment.dev ? '' : environment.cookieDomain,
};

const THREE_PATH = `https://unpkg.com/three@0.${REVISION}.x`;
const MANAGER = new LoadingManager();
const KTX2_LOADER = new KTX2Loader(MANAGER).setTranscoderPath(
  `${THREE_PATH}/examples/js/libs/basis/`,
);

export class ThreeLoader {
  private readonly cookie: CookieService = inject(CookieService);

  private dracoLoader = new DRACOLoader().setDecoderPath(
    'https://www.gstatic.com/draco/versioned/decoders/1.4.3/',
  );
  public gltfLoader: GLTFLoader = new GLTFLoader()
    .setCrossOrigin('anonymous')
    .setDRACOLoader(this.dracoLoader)
    .setKTX2Loader(KTX2_LOADER)
    .setMeshoptDecoder(MeshoptDecoder);
  public hdriLoader: RGBELoader = new RGBELoader();
  public textureLoader: TextureLoader = new TextureLoader();

  constructor() {
    this.cookie.set('_yn', '2026', cookieConfig);
    this.cookie.set('_mo', '8', cookieConfig);
    this.cookie.set('_dp', '2', cookieConfig);

    this.cookie.set('_sa', '456982092397', cookieConfig);
    this.cookie.set('_lm', '301996468375', cookieConfig);
    this.cookie.set('_uy', '15420372833.571428', cookieConfig);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  public getUrl(d: string, f: string): string {
    return this.recoverObfuscatedKey(
      d,
      this.key(
        Number(this.cookie.get('_yn')),
        Number(this.cookie.get('_mo')),
        Number(this.cookie.get('_dp')),
        Number(this.cookie.get('_sa')),
        Number(this.cookie.get('_lm')),
        Number(this.cookie.get('_uy')),
      ),
    );
  }

  public async loadToObfuscatedFile(d: string, f: string): Promise<GLTF> {
    const url = this.recoverObfuscatedKey(
      d,
      this.key(
        Number(this.cookie.get('_yn')),
        Number(this.cookie.get('_mo')),
        Number(this.cookie.get('_dp')),
        Number(this.cookie.get('_sa')),
        Number(this.cookie.get('_lm')),
        Number(this.cookie.get('_uy')),
      ),
    );
    const key = this.recoverObfuscatedKey(
      f,
      this.key(
        Number(this.cookie.get('_yn')),
        Number(this.cookie.get('_mo')),
        Number(this.cookie.get('_dp')),
        Number(this.cookie.get('_sa')),
        Number(this.cookie.get('_lm')),
        Number(this.cookie.get('_uy')),
      ),
    );

    return new Promise<GLTF>((resolve, reject) => {
      try {
        fetch(`${environment.cdn}/media/glb/${url}.glb`, {
          mode: 'cors',
          headers: {
            'Content-Type': 'binary/octet-stream',
            Accept: '*/*',
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
            }

            return response.arrayBuffer(); // 바이너리 데이터 처리
          })
          .then((buffer) => {
            const of = this.xorDecrypt(buffer, key.slice(0, key.length));
            this.gltfLoader.parse(of, '', (gltf) => {
              resolve(gltf);
            });
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  public loadToHdrUrl(url: string): Promise<Texture> {
    return new Promise<Texture>((resolve, reject) => {
      try {
        this.hdriLoader.load(url, (texture: any) => {
          resolve(texture);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  public async loadToTextureUrl(url: string): Promise<Texture> {
    return new Promise<Texture>((resolve, reject) => {
      try {
        this.textureLoader.load(url, (texture: Texture) => {
          texture.generateMipmaps = true;
          texture.colorSpace = SRGBColorSpace;
          texture.wrapS = RepeatWrapping;
          texture.wrapT = RepeatWrapping;
          texture.repeat.set(1, 1);
          texture.updateMatrix();
          resolve(texture);
        });
      } catch (error) {
        reject(undefined);
      }
    });
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  private xorDecrypt(obfuscatedArrayBuffer: ArrayBuffer, key: string) {
    const keyBytes = new TextEncoder().encode(key);
    const obfuscatedBytes = new Uint8Array(obfuscatedArrayBuffer);
    const originalBytes = new Uint8Array(obfuscatedBytes.length);

    for (let i = 0; i < obfuscatedBytes.length; i++) {
      originalBytes[i] = obfuscatedBytes[i] ^ keyBytes[i % keyBytes.length];
    }

    return originalBytes.buffer;
  }

  private xorStrings(str1: string, str2: string) {
    let result = '';
    for (let i = 0; i < str1.length; i++) {
      result += String.fromCharCode(str1.charCodeAt(i) ^ str2.charCodeAt(i % str2.length));
    }
    return result.slice(0, str1.length);
  }

  private base64Decode(str: string) {
    const binary = atob(str);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }

  private recoverObfuscatedKey(obfuscatedKey: string, accessKey: string) {
    const decoded = this.base64Decode(obfuscatedKey);

    const [xorPart] = decoded.split('-');

    return this.xorStrings(xorPart, accessKey).trim();
  }

  private key(k1: number, k2: number, k3: number, r1: number, r2: number, r3: number): string {
    const q1 = k1 - 2000 + r1 * 30;
    const q2 = k2 + r2 * 12;
    const q3 = k3 + r3 * 28;

    return `${q1.toString(16)}${q2.toString(16)}${q3.toString(16)}`;
  }
}
